<script setup lang="ts">
/**
 * CMS layout shell. Wraps the page area (its default slot).
 *
 * - Not an editor / production: renders the slot verbatim (SSR-identical, no lib
 *   cost). This is the "must not render unless CMS-enabled" guarantee.
 * - CMS-enabled, deploy-only (`CMS_EDITING` off — the default for now): renders
 *   the slot verbatim plus a floating DEPLOY button. No splitter, no editor.
 * - CMS-enabled editor with editing on: wraps the page in an `e-splitter` — left = the real page
 *   (one render = live preview), right = the editor, MOUNTED ONLY WHEN EXPANDED
 *   (so toggling Edit never re-mounts the page).
 */
const cms = useCmsEditor();

// The docs layout (<e-layout-docs>) is a full-viewport 100dvh shell with no
// external header, so the editor splitter must fill 100dvh too. Elsewhere a
// sticky header sits above it, so keep `100dvh - header` there. Without this,
// on docs the splitter is `100dvh - 4rem` around a 100dvh layout → the panel
// scrolls AND the layout scrolls (double scrollbar; the 4rem = header height).
const route = useRoute();
const isDocsLayout = computed(() => route.meta.layout === "docs");

// When the split is mounted (CMS available, even with the editor closed) the
// page scroll lives inside the e-splitter panel. The global header sits ABOVE
// the splitter, so header(4rem) + splitter(100dvh-4rem) sums to ~100dvh in
// #__nuxt — any sub-pixel drift (header not exactly 4rem) leaves the document a
// hair over the viewport and it scrolls ~1px behind the panel's real scrollbar.
// The `cms-frame` class pins that frame to the viewport and clips (see main.css).
useHead({
  htmlAttrs: {
    class: computed(() => (cms.editing.value ? "cms-frame" : "")),
  },
});

onMounted(() => cms.probe());

async function ensureElements() {
  const core = await import("@econnect/ui-core/define");
  core.defineSplitter();
  core.defineButton();
}
watch(
  () => cms.editing.value,
  (on) => {
    if (on) ensureElements();
  },
  { immediate: true },
);

function toggle() {
  cms.open.value = !cms.open.value;
}
</script>

<template>
  <slot v-if="!cms.editing.value" />
  <div v-if="cms.enabled.value && !cms.editing.value" class="fixed bottom-5 left-5 z-[9997]">
    <LazyCmsDeploy size="xs" />
  </div>
  <template v-if="cms.editing.value">
    <e-splitter
      direction="horizontal"
      class="block"
      :class="isDocsLayout ? 'h-dvh cms-splitter-docs' : 'h-[calc(100dvh-var(--header-height,4rem))]'"
    >
      <e-splitter-panel class="h-full min-h-0" min-size="30">
        <div class="h-full" :class="isDocsLayout ? 'overflow-hidden [contain:layout]' : 'overflow-y-auto'">
          <slot />
        </div>
      </e-splitter-panel>
      <template v-if="cms.open.value">
        <e-splitter-handle />
        <e-splitter-panel class="h-full min-h-0" default-size="40" min-size="26">
          <LazyCmsEditorPanel />
        </e-splitter-panel>
      </template>
    </e-splitter>

    <div v-show="!cms.open.value" class="fixed bottom-5 left-5 z-[9997]">
      <e-button size="xs" icon="edit" title="Edit this page" @click="toggle">Edit</e-button>
    </div>
  </template>
</template>
